import React, { useEffect, useState } from "react";
import Divider from "../../../components/Decors/Divider/Divider";
import { useTranslation } from "react-i18next";
import { Badge as BadgeModel } from "../../../models/badges";
import * as BadgeApi from "../../../network/collectibles_api";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import styles from "../BadgesPage.module.scss";
import { formatDate } from "../../../utils/formatDate";

const Badges = () => {
  const { t, i18n } = useTranslation();
  const [badges, setBadges] = useState<BadgeModel[]>([]);
  const [allBadges, setAllBadges] = useState<BadgeModel[]>([]);
  const [badgesLoading, setBadgesLoading] = useState(true);
  const [showBadgesLoadingError, setShowBadgesLoadingError] = useState(false);

  useEffect(() => {
    async function loadBadge() {
      try {
        setShowBadgesLoadingError(false);
        setBadgesLoading(true);
        const badges = await BadgeApi.fetchBadges();
        const allBadges = await BadgeApi.fetchAllBadges();
        setBadges(badges);
        setAllBadges(allBadges);
      } catch (error) {
        console.error(error);
        setShowBadgesLoadingError(true);
      } finally {
        setBadgesLoading(false);
      }
    }
    loadBadge();
  }, []);

  /**
   * Merges the user's earned badges with all available badges.
   *
   * @returns {Badge[]} An array of badges, where each badge contains details from either
   *                    the user's earned badges or the full badge list.
   */
  const mergedBadges: BadgeModel[] = Object.entries(allBadges).map(
    ([key, badge]) => {
      const userBadge = badges.find((b) => b.fileName === badge.fileName);
      return {
        _id: userBadge?._id || key,
        fileName: userBadge?.fileName || "locked",
        acquireDate: userBadge?.acquireDate,
        englishTitle: userBadge?.englishTitle || badge.englishTitle,
        hungarianTitle: userBadge?.hungarianTitle || badge.hungarianTitle,
        englishRequirement: badge.englishRequirement,
        hungarianRequirement: badge.hungarianRequirement,
      };
    }
  );

  return (
    <>
      <div className="title-block-container">
        <h1>{t("pages.badges")}</h1>
        <Divider />
      </div>
      {badgesLoading && (
        <Spinner className="custom-spinner" animation="border" />
      )}
      {showBadgesLoadingError && <p>{t("error.refresh")}</p>}

      {badges.length === 0 && !badgesLoading && !showBadgesLoadingError && (
        <p className={styles["no-badges-message"]}>
          {t("badges.noBadgesMessage")}
        </p>
      )}
      {mergedBadges && (
        <div className={styles["badges-container"]}>
          {mergedBadges.map((badge) => (
            <div key={badge._id} className={styles["badge-item"]}>
              <OverlayTrigger
                placement={"bottom"}
                overlay={
                  <Tooltip id={`tooltip-bottom`}>
                    {i18n.language === "hu"
                      ? badge.hungarianRequirement
                      : badge.englishRequirement}
                  </Tooltip>
                }
              >
                <div className={styles["badge-image-container"]}>
                  <img
                    src={`${process.env.REACT_APP_API_URL}/images/collectibles/badges/${badge.fileName}`}
                    alt={badge.fileName}
                    className="img-fluid"
                  />
                </div>
              </OverlayTrigger>
              <p className={styles["badge-title"]}>
                {i18n.language === "hu"
                  ? badge.hungarianTitle
                  : badge.englishTitle}
              </p>
              {badge.acquireDate && (
                <p className={styles["badge-date"]}>
                  {formatDate(badge.acquireDate, i18n.language)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default Badges;
